import { ADVISORS_BASE, WIL_AVATAR, WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants'
import { SEAT_CATEGORIES } from '@src/components/sco/Advisors/data/seatCategories'
import {
  bookMeetingHrefForAdvisor,
  bookMeetingLabelForAdvisor,
} from '@src/components/sco/Advisors/model/access'
import { resolveAdvisorsBase } from '@src/components/sco/Advisors/paths'
import { BaseballCardData } from '@src/components/sco/Advisors/types'
import { normalizeAsPath } from '@src/components/sco/Advisors/utils'
import { bookSessionLabel } from '@src/components/sco/Advisors/utils/sessionFormat'
import {
  ADVISOR_ENGAGEMENT_SOURCE_AI,
  ADVISOR_ENGAGEMENT_SOURCE_TEAM,
} from '@src/constants/advisor-engagement-sources'
import { ScoAdvisorEngagement } from '@src/graphql-types'

/** ADV-G2: prefer API/static category palette; FE seat list is the shared source with BE keys. */
const paletteForCategoryKey = (categoryKey?: string | null): string | undefined => {
  if (!categoryKey) {
    return undefined
  }
  return SEAT_CATEGORIES.find(c => c.key === categoryKey)?.palette
}

const SOURCE_KEY_FALLBACK_CATEGORY: Record<string, { title: string; palette: string }> = {
  [ADVISOR_ENGAGEMENT_SOURCE_AI]: { title: 'Always on', palette: 'teal' },
  [ADVISOR_ENGAGEMENT_SOURCE_TEAM]: { title: 'Advisor', palette: 'yellow' },
}

/** User-facing identity for the Wil AI tool (name/bio/avatar only — no vanity stats). */
export const WIL_DISPLAY = {
  name: WIL_DISPLAY_NAME,
  bio: 'Instant answers on any startup question — always on when you need a second opinion.',
  avatarUrl: WIL_AVATAR,
}

const formatStat = (value?: number | null) => {
  if (value == null) {
    return '—'
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`
  }
  return String(value)
}

export const memberProfileHref = (engagement: ScoAdvisorEngagement) => {
  const user = engagement.advisorUser
  if (!user) {
    return undefined
  }
  return `/members/${user.slug || user.userId}`
}

export const inboxAdvisorHref = (advisorUserId?: string | null, base: string = ADVISORS_BASE) => {
  const root = `${base}/inbox`
  return advisorUserId ? `${root}?advisorUserId=${encodeURIComponent(advisorUserId)}` : root
}

/** @deprecated Use inboxAdvisorHref — `/ask-advisors` is a 302 to Inbox. */
export const askAdvisorsHref = inboxAdvisorHref

export const askWilHref = (base: string = ADVISORS_BASE) => `${base}/ask`

export const paidSessionHref = (expertUserId: string, base: string = ADVISORS_BASE) =>
  `${base}/sessions/new?expertUserId=${encodeURIComponent(expertUserId)}`

export const engagementChatHref = (engagement: ScoAdvisorEngagement, base: string = ADVISORS_BASE) => {
  if (engagement.isAI) {
    return askWilHref(base)
  }
  const advisorUserId = engagement.advisorUserId || engagement.advisorUser?.userId
  return inboxAdvisorHref(advisorUserId, base)
}

export const isEngagementChatActive = (
  engagement: ScoAdvisorEngagement,
  asPath: string,
  selectedAdvisorUserId?: string
) => {
  const path = normalizeAsPath(asPath)
  const base = resolveAdvisorsBase(asPath)

  if (engagement.isAI) {
    // Only Wil's own routes — not ask-advisors (which shares the .../ask prefix).
    return path === askWilHref(base)
  }

  const advisorUserId = engagement.advisorUserId || engagement.advisorUser?.userId
  if (!advisorUserId) {
    return false
  }

  const onInbox = path === `${base}/inbox` || path.startsWith(`${base}/inbox?`)
  const onLegacyAsk = path.startsWith(`${base}/ask-advisors`)
  if (!onInbox && !onLegacyAsk) {
    return false
  }

  return selectedAdvisorUserId === advisorUserId
}

const categoryMeta = (engagement: ScoAdvisorEngagement) => {
  const categoryKey = engagement.categoryKey || engagement.category?.key
  // Prefer BE category title; palette from shared category keys (API reference parity)
  const palette =
    paletteForCategoryKey(categoryKey) ||
    (engagement.sourceKey && SOURCE_KEY_FALLBACK_CATEGORY[engagement.sourceKey]?.palette) ||
    'gray'
  const title =
    engagement.category?.title ||
    (engagement.sourceKey && SOURCE_KEY_FALLBACK_CATEGORY[engagement.sourceKey]?.title) ||
    'Advisor'

  return { title, palette }
}

/** Label + color palette for the category badge shown next to an advisor. */
export const engagementCategoryBadge = (
  engagement: ScoAdvisorEngagement
): { label: string; palette: string } => {
  if (engagement.isAI) {
    return { label: 'Always on', palette: 'teal' }
  }

  const categoryKey = engagement.categoryKey || engagement.category?.key
  const palette = paletteForCategoryKey(categoryKey) || 'gray'

  if (engagement.category?.title) {
    return { label: engagement.category.title, palette }
  }
  if (engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM) {
    return { label: 'Startups.com team', palette: 'yellow' }
  }
  return { label: 'Advisor', palette: 'gray' }
}

/**
 * Prefer backend names for board rows.
 * AI tool users (e.g. firstName "Startups.com" + lastName "Wil.ai") show lastName only.
 */
export const engagementDisplayName = (engagement: ScoAdvisorEngagement) => {
  const user = engagement.advisorUser
  if (engagement.isAI) {
    return user?.lastName || user?.fullName || engagement.advisorName || WIL_DISPLAY_NAME
  }
  return user?.fullName || user?.displayName || engagement.advisorName || 'Advisor'
}

/**
 * Free-text blurb for advisor cards (BaseballCard, AdvisorMiniProfile).
 * Prefer real profile copy (shortBio → bio). Do NOT use structured
 * `bioForAdvising` as-is — become-advisor stores "Notable brands: …" lines there.
 */
const freeformFromBioForAdvising = (bioForAdvising?: string | null): string => {
  if (!bioForAdvising?.trim()) {
    return ''
  }
  const free = bioForAdvising
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(Boolean)
    .filter(
      p => !/^Notable brands:/i.test(p) && !/^Top skills:/i.test(p) && !/^How founders can engage:/i.test(p)
    )
  return free.join('\n\n').trim()
}

const displayBio = (engagement: ScoAdvisorEngagement) => {
  if (engagement.isAI) {
    return engagement.advisorUser?.shortBio || engagement.advisorUser?.bio || WIL_DISPLAY.bio
  }

  const user = engagement.advisorUser
  const fromUser = user?.shortBio?.trim() || user?.bio?.trim() || ''
  if (fromUser) {
    return fromUser
  }

  const freeformNote = freeformFromBioForAdvising(user?.advisorProfile?.bioForAdvising)
  if (freeformNote) {
    return freeformNote
  }

  return user?.advisorProfile?.headline?.trim() || ''
}

const displayAvatar = (engagement: ScoAdvisorEngagement) => {
  if (engagement.advisorUser?.pictureUrl) {
    return engagement.advisorUser.pictureUrl
  }
  return engagement.isAI ? WIL_DISPLAY.avatarUrl : ''
}

export const engagementToBaseballCard = (
  engagement: ScoAdvisorEngagement,
  options?: { base?: string }
): BaseballCardData => {
  const base = options?.base ?? ADVISORS_BASE
  const { title: role, palette: rolePalette } = categoryMeta(engagement)
  const advisorUserId = engagement.advisorUserId || engagement.advisorUser?.userId
  const profile = engagement.advisorUser?.advisorProfile
  const name = engagementDisplayName(engagement)

  if (engagement.isAI) {
    return {
      name,
      role: 'Always on',
      rolePalette: 'teal',
      bio: displayBio(engagement),
      avatarUrl: displayAvatar(engagement),
      responses: formatStat(profile?.responsesCount),
      founders: formatStat(profile?.foundersHelpedCount),
      isAI: true,
      primaryLabel: `Ask ${name}`,
      primaryHref: askWilHref(base),
      secondaryLabel: 'View',
    }
  }

  const officeHoursHref = bookMeetingHrefForAdvisor({
    name,
    isAI: false,
    userId: advisorUserId,
    sessionBookingUrl: profile?.sessionBookingUrl,
  })
  const sessionHref =
    profile?.canAcceptSessions && advisorUserId ? paidSessionHref(advisorUserId, base) : undefined

  return {
    name,
    role,
    rolePalette,
    bio: displayBio(engagement),
    avatarUrl: displayAvatar(engagement),
    responses: formatStat(profile?.responsesCount),
    founders: formatStat(profile?.foundersHelpedCount),
    primaryLabel: 'Message',
    primaryHref: inboxAdvisorHref(advisorUserId, base),
    primaryPaywall: engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM ? 'team' : 'paid',
    ...(sessionHref
      ? {
          secondaryLabel: bookSessionLabel(),
          secondaryHref: sessionHref,
          secondaryExternal: false,
          ...(officeHoursHref
            ? {
                tertiaryLabel: bookMeetingLabelForAdvisor(name),
                tertiaryHref: officeHoursHref,
                tertiaryExternal: true,
              }
            : {}),
        }
      : officeHoursHref
        ? {
            secondaryLabel: bookMeetingLabelForAdvisor(name),
            secondaryHref: officeHoursHref,
            secondaryExternal: true,
          }
        : {}),
  }
}

export const isHumanBoardAdvisor = (engagement?: ScoAdvisorEngagement | null) =>
  !!engagement && !engagement.isAI && !!engagement.advisorUser
